import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { setPatientToken } from '@/lib/auth-token'
import { clearCachedEntitlement } from '@/lib/entitlement-cache'
import {
  cancelTransferCode,
  claimPendingTransfer,
  clearIssuedCode,
  clearPendingTransfer,
  fetchTransferCodeState,
  issueTransferCode,
  readIssuedCode,
  redeemTransferCode,
  type TransferRedeemResponse,
} from '@/lib/transfer'
import { clearRetired, markRetired } from '@/lib/transfer-retirement'
import { clearTrainingHistory } from '@/lib/training-history-cache'
import {
  trackTransferClaimed,
  trackTransferCodeIssued,
  trackTransferRedeemFailed,
  trackTransferRedeemed,
  trackTransferRetired,
} from '@/lib/telemetry-events'
import { useAuthStore } from '@/stores/auth'
import { useEntitlementStore } from '@/stores/entitlement'
import { useExercisesStore } from '@/stores/exercises'
import { useHomeworkStore } from '@/stores/homework'
import { usePatientSettingsStore } from '@/stores/patientSettings'
import { useSupportCodeStore } from '@/stores/supportCode'

/**
 * Device-to-device account transfer. See docs/data-transfer.md.
 *
 * Two roles, one store, because a device is only ever in one of them:
 *
 *   **Issuing** — this device mints a code and waits. It learns the transfer
 *   completed by its own poll coming back 401: redemption revokes every token
 *   the account held, and nothing else kills a live token mid-session.
 *
 *   **Redeeming** — this device types the code in, is handed a token for the
 *   surviving account, and swaps identity.
 *
 * The swap is the delicate half, so it is deliberately blunt: rather than
 * reasoning cache by cache about what is still true, `applyTransfer` resets
 * every patient-scoped store and cache and rehydrates from the server. The one
 * thing it must get right is ordering — the token goes first, because every
 * rehydrate call authenticates with it.
 */
export const useTransferStore = defineStore('transfer', () => {
  /** The plaintext code this device is currently offering, if any. */
  const issuedCode = ref<string | null>(null)
  const issuedExpiresAt = ref<string | null>(null)

  const issuing = ref(false)
  const redeeming = ref(false)

  /** Server-reported refusal, shown verbatim. */
  const lastError = ref<string | null>(null)

  /** Set once the other device has taken the account over. */
  const retired = ref(false)

  /** What the surviving account holds, for the success screen. */
  const imported = ref<{ exercises: number; runs: number } | null>(null)

  const hasIssuedCode = computed(() => issuedCode.value !== null)

  /** Seconds left on the offered code; 0 once it has lapsed. */
  function secondsRemaining(now: number = Date.now()): number {
    if (issuedExpiresAt.value === null) return 0
    const at = Date.parse(issuedExpiresAt.value)
    if (Number.isNaN(at)) return 0
    return Math.max(0, Math.floor((at - now) / 1000))
  }

  // --- Issuing -------------------------------------------------------------

  /** Re-adopt a code issued before the app was backgrounded. */
  async function restoreIssuedCode(): Promise<void> {
    const stored = await readIssuedCode()
    if (stored === null) return
    issuedCode.value = stored.code
    issuedExpiresAt.value = stored.expires_at
  }

  async function issue(): Promise<void> {
    if (issuing.value) return
    issuing.value = true
    lastError.value = null
    try {
      const issued = await issueTransferCode()
      issuedCode.value = issued.code
      issuedExpiresAt.value = issued.expires_at
      trackTransferCodeIssued()
    } catch (e) {
      lastError.value = messageFor(e)
      throw e
    } finally {
      issuing.value = false
    }
  }

  async function cancel(): Promise<void> {
    issuedCode.value = null
    issuedExpiresAt.value = null
    lastError.value = null
    try {
      await cancelTransferCode()
    } catch {
      // The code expires on its own in minutes; a failed cancel is cosmetic.
      await clearIssuedCode()
    }
  }

  /**
   * One poll tick on the issuing device. Returns true once this device has been
   * retired, which is the dialog's cue to show its terminal state.
   *
   * A 401 here *is* the completion signal — see the store docblock. The 401
   * interceptor in lib/api.ts has already discarded the token and recorded the
   * tombstone by the time this resolves, so all that is left is to say so.
   */
  async function pollIssuedCode(): Promise<boolean> {
    if (retired.value) return true
    try {
      const state = await fetchTransferCodeState()
      if (state.state === 'redeemed') {
        await retire()
        return true
      }
      if (state.state === 'none') {
        issuedCode.value = null
        issuedExpiresAt.value = null
      }
      return false
    } catch (e) {
      if (statusOf(e) === 401) {
        await retire()
        return true
      }
      // Offline: keep showing the code. It is still valid server-side, and the
      // other device can still redeem it.
      return false
    }
  }

  /**
   * Put this device into its retired state: the account lives elsewhere now.
   *
   * Everything patient-scoped goes, including the token, so nothing of the
   * previous account is readable on a device that no longer owns it.
   */
  async function retire(): Promise<void> {
    retired.value = true
    issuedCode.value = null
    issuedExpiresAt.value = null
    await markRetired(new Date().toISOString())
    await clearIssuedCode()
    await wipeLocalAccountState()
    trackTransferRetired()
  }

  // --- Redeeming -----------------------------------------------------------

  async function redeem(rawCode: string): Promise<void> {
    if (redeeming.value) return
    redeeming.value = true
    lastError.value = null
    try {
      const response = await redeemTransferCode(rawCode)
      await applyTransfer(response)
      trackTransferRedeemed({
        exercises: response.imported.exercises,
        runs: response.imported.runs,
      })
    } catch (e) {
      lastError.value = messageFor(e)
      trackTransferRedeemFailed(reasonFor(e))
      throw e
    } finally {
      redeeming.value = false
    }
  }

  /**
   * Finish a redemption whose response was lost in transit. Safe to call on
   * every launch: it resolves null when there is nothing outstanding, which is
   * every launch but the unlucky one.
   */
  async function resumePendingRedemption(): Promise<boolean> {
    const response = await claimPendingTransfer()
    if (response === null) return false
    await applyTransfer(response)
    trackTransferClaimed()
    return true
  }

  /**
   * Adopt the surviving account on this device.
   *
   * Order is load-bearing: the token first, because every call below
   * authenticates with it; the caches next, so nothing of the previous account
   * can be read back by a rehydrate that races; then the refetches.
   */
  async function applyTransfer(response: TransferRedeemResponse): Promise<void> {
    await setPatientToken(response.token)
    await clearPendingTransfer()
    await clearRetired()

    const settings = usePatientSettingsStore()
    const supportCode = useSupportCodeStore()
    const entitlement = useEntitlementStore()
    const auth = useAuthStore()
    const exercises = useExercisesStore()
    const homework = useHomeworkStore()

    await clearTrainingHistory()
    await settings.reset()
    await supportCode.reset()

    // Apply the entitlement the response already carries before anything slow
    // runs, so the paywall state is correct on the first frame after the
    // dialog closes rather than after a round-trip. This overwrites the cached
    // copy rather than clearing it, so there is no window in which a cold
    // launch would read the previous account's trial clock.
    await entitlement.adoptEntitlement(response)

    // Re-identifies for telemetry and error reporting under the surviving id,
    // and repopulates practiceName — which is what gates the Hausaufgaben pill.
    await auth.bootstrapPatient()

    await settings.hydrate()

    // Exercises and homework are refetched rather than reset-and-lazy-loaded:
    // the whole point of the transfer is that this content is now here, and an
    // empty picker would read as a failed transfer.
    await Promise.allSettled([exercises.refresh(), homework.refresh()])

    imported.value = response.imported
  }

  /**
   * Strip every patient-scoped trace from this device. Used by `retire()`; the
   * redeeming side does the same work piecewise in `applyTransfer`, where it
   * can rehydrate immediately afterwards instead of leaving blanks.
   */
  async function wipeLocalAccountState(): Promise<void> {
    const auth = useAuthStore()
    const settings = usePatientSettingsStore()
    const supportCode = useSupportCodeStore()

    await clearCachedEntitlement()
    await clearTrainingHistory()
    await settings.reset()
    await supportCode.reset()
    await auth.logout()
  }

  return {
    // state
    issuedCode,
    issuedExpiresAt,
    issuing,
    redeeming,
    lastError,
    retired,
    imported,
    // getters
    hasIssuedCode,
    secondsRemaining,
    // actions
    restoreIssuedCode,
    issue,
    cancel,
    pollIssuedCode,
    retire,
    redeem,
    resumePendingRedemption,
  }
})

function statusOf(e: unknown): number | null {
  if (e === null || typeof e !== 'object' || !('response' in e)) return null
  const status = (e as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' ? status : null
}

/**
 * The server's own wording where it has one — it distinguishes "wrong code"
 * from "not available on this device" and only it knows which. Falls back to a
 * network message, since an axios error's own text is no use to a patient.
 */
function messageFor(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'response' in e) {
    const data = (e as { response?: { data?: { message?: unknown } } }).response?.data
    if (typeof data?.message === 'string' && data.message !== '') return data.message
  }
  return 'Keine Verbindung. Bitte prüfe die Internetverbindung und versuche es erneut.'
}

/** Coarse buckets, so a brute-force campaign is visible without logging codes. */
function reasonFor(e: unknown): 'invalid' | 'rate_limited' | 'network' | 'server' {
  const status = statusOf(e)
  if (status === 422) return 'invalid'
  if (status === 429) return 'rate_limited'
  if (status === null) return 'network'
  return 'server'
}
